<template>
  <div>
    <div id="simple-input">
      <tokenized-input
        :tokens="queryElements"
        :menus="queryMenus"
        :labelText="$t('ESTERO_SIMPLE_LABEL')"
        :helpLink="$t('ESTERO_SIMPLE_HELP_LINK')"
        @add="onTokenAdd"
        @menuShown="onQueryMenuShown"
        @menuHidden="onQueryMenuHidden"
      >
        <template #queryProp="{ model }">
          <div
            class="ui left labeled tiny query button"
            :class="{ active: menuShownForToken === model.id }"
          >
            <a class="ui tiny query label" :class="{ active: menuShownForToken === model.id }"
              ><span class="prop">{{ model.prop }}</span>
              <span class="op">{{ $t(model.op && model.op.i18n) }}</span>
              <span class="value">{{ model.value }}</span></a
            >
            <div
              class="ui icon tiny button"
              @click.stop="removeToken(model)"
              :class="{ active: menuShownForToken === model.id }"
            >
              <i class="ui close icon"></i>
            </div>
          </div>
        </template>

        <template #queryProp.menu="{ model, token }">
          <div class="ui vertical menu">
            <div class="item">
              <div class="header">{{ $t('ESTERO_SIMPLE_MENU_PROP') }}</div>
              <div class="menu" v-if="properties && properties.length > 10">
                <div class="item">
                  <div class="ui tiny icon input">
                    <input type="text" v-model="settingsSearchTerm" />
                    <i class="ui search icon"></i>
                  </div>
                </div>
              </div>
              <div class="scroller menu">
                <a
                  v-for="p in model.properties"
                  :key="p"
                  class="item"
                  @click="token.prop = p"
                  :class="{ active: p === token.prop }"
                >
                  {{ p }}
                </a>
              </div>
            </div>
            <div class="item">
              <div class="header">{{ $t('ESTERO_SIMPLE_MENU_OPERATOR') }}</div>
              <div class="scroller menu">
                <a
                  v-for="op in model.operators"
                  :key="op.symbol"
                  class="item"
                  @click="token.op = op"
                  :class="{ active: op.symbol === (token.op && token.op.symbol) }"
                >
                  {{ $t(op.i18n) }}
                </a>
              </div>
            </div>
            <div class="item">
              <div class="header">{{ $t('ESTERO_SIMPLE_MENU_VALUE') }}</div>
              <div class="menu">
                <div class="item">
                  <div class="ui icon input">
                    <input type="text" v-model="token.value" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </tokenized-input>
      <div class="run-buttons">
        <div class="ui right floated small icon buttons" style="margin-top: 5px;">
          <div class="ui button" @click="reset">
            <i class="ui recycle icon"></i>
          </div>
          <div class="ui green button" @click="run">
            <i class="ui running icon"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

#simple-input {
  display: flex;
  flex-direction: row;
  align-items: center;
  .run-buttons {
    margin: auto;
    padding-left: 7px;
    padding-top: 17px;
  }
}

.query.button {
  .op {
    padding-right: 3px;
    padding-left: 3px;
    font-weight: normal;
  }

  .prop,
  .value {
    font-weight: bold;
  }

  margin: 2px;
}

.scroller {
  max-height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  text-overflow: ellipsis;
}
</style>

<script>
// @flow
import Vue from 'vue'
import { Estero } from 'services'
import { TokenizedInput } from 'ui'

export default {
  name: 'estero-simple',
  components: { TokenizedInput },
  props: ['code', 'operators', 'properties'],
  data() {
    return {
      queryElements: [],
      settingsSearchTerm: null,
      menuShownForToken: null
    }
  },

  computed: {
    filteredSettings() {
      if (this.settingsSearchTerm) {
        return _.filter(
          this.properties,
          p => p.toLowerCase().indexOf(this.settingsSearchTerm.toLowerCase()) >= 0
        )
      }
      return this.properties
    },
    queryMenus() {
      return [
        {
          template: 'queryProp',
          properties: this.filteredSettings,
          operators: _.values(this.operators)
        }
      ]
    },
    queryElementsFromCode() {
      // Map the individual elements of the query to queryElements
      return _.filter(
        _.map(
          this.code
            .replaceAll('(', '')
            .replaceAll(')', '')
            .split(' AND '),
          elm => {
            var operator = _.find(this.operators, op => op.match && op.match.test(elm))
            if (!operator) return null
            var operands = operator.match.exec(elm)
            return {
              template: 'queryProp',
              id: Math.random() * 1000,
              prop: operands[operator.matchReverseOperands ? 2 : 1],
              op: operator,
              value: operands[operator.matchReverseOperands ? 1 : 2].replace(/"/g, '')
            }
          }
        ),
        queryElm => queryElm != null
      )
    }
  },

  methods: {
    removeToken(token) {
      this.queryElements = _.filter(this.queryElements, q => q.id !== token.id)
    },
    onTokenAdd() {
      this.queryElements.push({
        template: 'queryProp',
        id: Math.random() * 1000,
        prop: '?',
        op: this.operators['=='],
        value: '?'
      })
    },
    formatValue(op, v) {
      if (!op.forceStringArg && (/^\d+$/.test(v) || v === 'true' || v === 'false')) return v
      return op.symbol == 'regex' ? v : `"${v}"`
    },
    tokensToQuery(tokens) {
      return _.reduce(
        _.map(tokens, t => t.op.gen(t.prop, this.formatValue(t.op, t.value))),
        (memo, clause) => {
          memo = memo ? `(${memo} AND ${clause})` : clause
          return memo
        },
        ''
      )
    },
    run() {
      var code = this.tokensToQuery(this.queryElements)
      console.debug('code', code)
      this.$emit('run-code', code)
    },
    reset() {
      this.queryElements = []
    },
    onQueryMenuShown(m) {
      this.menuShownForToken = m.token.id
    },
    onQueryMenuHidden() {
      this.menuShownForToken = null
    }
  },

  mounted() {
    this.queryElements = this.queryElementsFromCode
  },

  watch: {
    code: function(c) {
      // Remove return and ; and split
      this.queryElements = this.queryElementsFromCode
    }
  }
}
</script>
